import {Observable} from 'rxjs';
import {CpmCApiBridge} from '../../../../../src/app/cpmc-api-bridge';
import {CpmCQueryPaginatorConfig, CpmCQueryPaginatorConfigUtils} from '../../cpmc-defines';
import {CpmCObjectStoreEntryProxyApiModel} from '../../models/c-customer/cpmc-object-store-entry-proxy.api-model';
import {CpmCObjectStoreEntryProxyCreateApiSchema} from '../../schemas/c-customer/cpmc-object-store-entry-proxy.api-schemas';
import {AllowedQueryParamTypes, QueryParams} from '../../../../../sdk/src/utils/query.utils';
import {CpmCPaginatedResultAPISchema} from '../../schemas/cpmc-paginated-result.api-schemas';

export type CpmCGetObjectStoreEntriesProxyParams = {
    [K in keyof CpmCObjectStoreEntryProxyApiModel]?: CpmCObjectStoreEntryProxyApiModel[K] extends AllowedQueryParamTypes
        ? CpmCObjectStoreEntryProxyApiModel[K]
        : never;
} & {} & QueryParams;

/**
 * ObjectStoreEntryProxy Api Service
 */
export class CpmCObjectStoreEntryProxyApiService {
    public static readonly dPaginatorConfig: CpmCQueryPaginatorConfig = {limit: -1, sort: 'key', order: 1};

    // ===============================================================
    // ===============================================================

    public static baseUrl(inCustomerUUID: string | undefined, linkObjectType: string, linkObjectUUID: string) {
        const base = CpmCApiBridge.getBackendBaseURL() + 'api/core/v1/';
        const customerUUID = CpmCApiBridge.getCurrentCustomerUUID();

        return (
            base +
            `customer/${customerUUID || inCustomerUUID}/object-store-entry-proxy/${linkObjectType}/${linkObjectUUID}`
        );
    }

    // ===============================================================
    // ===============================================================

    public static endpointList(
        inCustomerUUID: string | undefined,
        linkObjectType: string,
        linkObjectUUID: string
    ): string {
        return `${CpmCObjectStoreEntryProxyApiService.baseUrl(inCustomerUUID, linkObjectType, linkObjectUUID)}/list`;
    }
    public static endpointGet(
        inCustomerUUID: string | undefined,
        linkObjectType: string,
        linkObjectUUID: string,
        key: string
    ): string {
        return `${CpmCObjectStoreEntryProxyApiService.baseUrl(inCustomerUUID, linkObjectType, linkObjectUUID)}/${key}`;
    }
    public static endpointSet(
        inCustomerUUID: string | undefined,
        linkObjectType: string,
        linkObjectUUID: string,
        key: string
    ): string {
        return `${CpmCObjectStoreEntryProxyApiService.baseUrl(inCustomerUUID, linkObjectType, linkObjectUUID)}/${key}/set`;
    }
    public static endpointRevert(
        inCustomerUUID: string | undefined,
        linkObjectType: string,
        linkObjectUUID: string,
        key: string,
        history_index: number
    ): string {
        return `${CpmCObjectStoreEntryProxyApiService.baseUrl(inCustomerUUID, linkObjectType, linkObjectUUID)}/${key}/revert?history_index=${history_index}`;
    }
    public static endpointDelete(
        inCustomerUUID: string | undefined,
        linkObjectType: string,
        linkObjectUUID: string,
        key: string
    ): string {
        return `${CpmCObjectStoreEntryProxyApiService.baseUrl(inCustomerUUID, linkObjectType, linkObjectUUID)}/${key}/delete`;
    }

    // ===============================================================
    // ===============================================================

    public static getObjectStoreEntryProxyList(
        linkObjectType: string,
        linkObjectUUID: string,
        params: CpmCGetObjectStoreEntriesProxyParams = {},
        paginatorConfig: CpmCQueryPaginatorConfig = CpmCObjectStoreEntryProxyApiService.dPaginatorConfig
    ): Observable<CpmCPaginatedResultAPISchema<CpmCObjectStoreEntryProxyApiModel>> {
        return CpmCApiBridge.httpGet<CpmCPaginatedResultAPISchema<CpmCObjectStoreEntryProxyApiModel>>(
            CpmCObjectStoreEntryProxyApiService.endpointList(undefined, linkObjectType, linkObjectUUID),
            {
                ...(params as QueryParams),
                ...CpmCQueryPaginatorConfigUtils.toQueryParams(paginatorConfig)
            }
        );
    }
    public static getObjectStoreEntryProxy(
        linkObjectType: string,
        linkObjectUUID: string,
        key: string
    ): Observable<CpmCObjectStoreEntryProxyApiModel> {
        return CpmCApiBridge.httpGet<CpmCObjectStoreEntryProxyApiModel>(
            CpmCObjectStoreEntryProxyApiService.endpointGet(undefined, linkObjectType, linkObjectUUID, key)
        );
    }
    public static setObjectStoreEntryProxy(
        linkObjectType: string,
        linkObjectUUID: string,
        key: string,
        payload: CpmCObjectStoreEntryProxyCreateApiSchema
    ): Observable<CpmCObjectStoreEntryProxyApiModel> {
        return CpmCApiBridge.httpPost<CpmCObjectStoreEntryProxyApiModel>(
            CpmCObjectStoreEntryProxyApiService.endpointSet(undefined, linkObjectType, linkObjectUUID, key),
            payload
        );
    }
    public static revertObjectStoreEntryProxy(
        linkObjectType: string,
        linkObjectUUID: string,
        key: string,
        history_index: number
    ): Observable<CpmCObjectStoreEntryProxyApiModel> {
        return CpmCApiBridge.httpPost<CpmCObjectStoreEntryProxyApiModel>(
            CpmCObjectStoreEntryProxyApiService.endpointRevert(
                undefined,
                linkObjectType,
                linkObjectUUID,
                key,
                history_index
            ),
            null
        );
    }
    public static deleteObjectStoreEntryProxy(
        linkObjectType: string,
        linkObjectUUID: string,
        key: string
    ): Observable<boolean> {
        return CpmCApiBridge.httpDelete<boolean>(
            CpmCObjectStoreEntryProxyApiService.endpointDelete(undefined, linkObjectType, linkObjectUUID, key)
        );
    }
}
